import { ref, type Ref } from 'vue';
import { useModal } from 'vue-final-modal';
import { ConfirmationModal } from '@/shared/ui';
import CheckCircleIcon from '@/shared/assets/icons/CheckCircleIcon.svg';
import { useCustomToast } from '@/shared/lib';
import { trainingApi, type TrainingDetail } from '@/entities/Training';

export const COMPLETE_TRAINING_CONFIRM_TITLE = 'Tem certeza que deseja concluir o Treinamento?';
/**
 * Carries only what the AC04 promises. The design also asks the user to change a
 * collaborator manually to "Não concluído", but that per-row action does not exist
 * yet, so the sentence was dropped rather than left pointing at nothing. It comes
 * back with the individual-status story.
 */
export const COMPLETE_TRAINING_CONFIRM_TEXT =
  'Ao concluir o treinamento, todos os colaboradores Pendentes recebem a etiqueta de Concluído.';
export const COMPLETE_TRAINING_SUCCESS_MESSAGE = 'Treinamento concluído com sucesso';
export const COMPLETE_TRAINING_ERROR_MESSAGE = 'Erro ao concluir Treinamento. Tente novamente';

interface UseCompleteTrainingOptions {
  /** Reads the training on screen. Null while it is still loading. */
  training: () => TrainingDetail | null;
  /** Applies the training the endpoint returned, refreshing the screen without a refetch. */
  applyTraining: (training: TrainingDetail) => void;
  /**
   * Announces a completion that already succeeded, so views the endpoint response
   * does not cover — the paginated Colaboradores tab — can reload themselves.
   */
  onCompleted?: () => void;
}

export function useCompleteTraining(options: UseCompleteTrainingOptions): {
  isCompleting: Ref<boolean>;
  requestCompletion: () => void;
} {
  const { showSuccessToast, showErrorToast } = useCustomToast();

  const isCompleting = ref(false);

  /**
   * The in-flight guard lives here as well as in the modal, so a second confirm
   * can never start a second request even if the click reaches this by another
   * route. On failure the modal stays open with the button re-enabled.
   */
  const confirmCompletion = async () => {
    const training = options.training();
    if (isCompleting.value || !training) return;

    isCompleting.value = true;

    try {
      const updated = await trainingApi.completeTraining(training.id);
      completeModal.close();
      options.applyTraining(updated);
      options.onCompleted?.();
      showSuccessToast({ title: COMPLETE_TRAINING_SUCCESS_MESSAGE, type: 'success' });
    } catch {
      showErrorToast(
        { title: COMPLETE_TRAINING_ERROR_MESSAGE, type: 'error' },
        { timeout: false, closeButton: true }
      );
    } finally {
      isCompleting.value = false;
    }
  };

  const completeModal = useModal({
    component: ConfirmationModal,
    attrs: {
      icon: CheckCircleIcon,
      iconAlt: 'Ícone de conclusão',
      title: COMPLETE_TRAINING_CONFIRM_TITLE,
      text: COMPLETE_TRAINING_CONFIRM_TEXT,
      actionButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      get isSubmitting() {
        return isCompleting.value;
      },
      onClose() {
        if (isCompleting.value) return;
        completeModal.close();
      },
      async onConfirm() {
        await confirmCompletion();
      },
    },
  });

  const requestCompletion = () => {
    completeModal.open();
  };

  return { isCompleting, requestCompletion };
}
